import createTransform from 'redux-persist/es/createTransform'
import type { SessionState } from '../types/auth.types'
import { AUTH_PROVIDER, getAuthProviderType } from './environment'
import { getResolvedAuthProvider } from './authFeature'

/** Fields that must never reach storage under the hardened MS_BACKEND flow. */
const MEMORY_ONLY_FIELDS = ['token', 'refreshToken', 'tokenExpiresAtMs']

/**
 * Keeps the access/refresh tokens out of storage for MS_BACKEND ONLY.
 *
 * Why this is provider-conditional rather than a flat `blacklist`:
 *
 * `sessionPersistConfig` governs the ONE `session` slice that all three auth
 * providers share, so a flat blacklist applied the MS_BACKEND rule to COGNITO
 * and MSAL as well — and only MS_BACKEND can survive it. It re-acquires the
 * access token from the HttpOnly session cookie on every mount, so losing the
 * stored copy costs it nothing. The other two have no boot-time recovery:
 *
 *   - COGNITO: `session.token` IS the bearer and `session.refreshToken` is what
 *     the interceptor's 403/409 recovery branch needs. With both blacklisted, a
 *     reload rendered the app with no Authorization header (every call came back
 *     403 AccessDeniedException from the gateway) AND disabled the one path that
 *     could have recovered. Permanent until a fresh interactive login.
 *   - MSAL: can re-derive a token from MSAL's own sessionStorage cache, but the
 *     guard renders children before that lands, so a reload had the same effect.
 *
 * The failure was silent in both: `isAuthenticated` is NOT blacklisted, so it
 * rehydrates `true` while the token rehydrates `null`, and the guards — which
 * read `isAuthenticated` as a proxy for "we have a token" — render the app
 * anyway. Nothing throws; requests simply go out unauthenticated.
 *
 * Provider resolution falls back to the build-time env value, which matters at
 * first write before `/auth/feature` has settled. In practice a token only
 * exists after the provider is known, so the fallback is a safety net.
 *
 * Outbound nulls the same fields for MS_BACKEND so values written before the
 * provider was resolved (or by an earlier build) cannot rehydrate into a
 * hardened session.
 */
function isMsBackend(): boolean {
  return (
    (getResolvedAuthProvider() ?? getAuthProviderType()) ===
    AUTH_PROVIDER.MS_BACKEND
  )
}

/**
 * NOTE: redux-persist invokes a transform ONCE PER FIELD of the slice, not once
 * for the whole object — `state` is the field's value and `key` is its name.
 * (See the same note on `resetTransientSessionTransform`.)
 *
 * Fields are nulled rather than returned as `undefined`: each field is
 * serialized independently, and `JSON.stringify(undefined)` yields `undefined`
 * rather than a string. `null` is also the `initialState` value for all three,
 * so a hardened session rehydrates exactly as a fresh one would.
 */
export const dropTokensForMsBackendTransform = createTransform<
  unknown,
  unknown,
  SessionState,
  SessionState
>(
  // inbound (state → storage)
  (inboundState, key) => {
    if (MEMORY_ONLY_FIELDS.includes(key as string) && isMsBackend()) {
      return null
    }
    return inboundState
  },
  // outbound (storage → state)
  (outboundState, key) => {
    if (MEMORY_ONLY_FIELDS.includes(key as string) && isMsBackend()) {
      return null
    }
    return outboundState
  }
)
